import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmail } from '@/lib/api/auth'; 
import { LoginFormData } from '@/lib/auth/validation';
import { getAuthErrorMessage } from '@/lib/utils/error';
import { useAuthStore } from '@/store/authStore';
import { AUTH_ROUTES } from '@/lib/constants/auth';
import type { User } from '@/types/auth'; // Import User type

export const useAuth = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();
  // No need to get refreshUser or setInitialized from store here for signIn itself

  const signIn = async (data: LoginFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // signInWithEmail from lib/api/auth.ts authenticates with Supabase
      // and returns the user profile from your 'users' table.
      const { user: userProfile, session } = await signInWithEmail(data);

      if (!userProfile || !session) {
        throw new Error("Sign in failed, no user profile or session returned.");
      }

      // Directly update the authStore with the fetched user profile.
      // This makes the user state available immediately for App.tsx.
      // The onAuthStateChange listener in App.tsx will also fire and call refreshUser,
      // which is fine; it will essentially re-confirm the state or fetch if somehow missed.
      useAuthStore.setState({
        user: userProfile as User, // Cast if necessary, ensure signInWithEmail returns compatible User type
        isLoading: false,
        initialized: true, 
        error: null,
      });

      navigate(AUTH_ROUTES.DASHBOARD, { replace: true });

    } catch (error) {
      const message = getAuthErrorMessage(error);
      setSubmitError(message);
      // Ensure user is cleared and state is updated in the store on error
      useAuthStore.setState({ 
        user: null, 
        isLoading: false, 
        initialized: true, // Still initialized, as an attempt was made
        error: message 
      });
      // No need to re-throw if SignInForm handles submitError
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    signIn,
    isLoading: isSubmitting, // This hook's isLoading is for the form submission process
    error: submitError,     // This hook's error is for the form submission process
  };
};